import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useTranslation } from "react-i18next";
import { messageText } from "@/i18n/messageTranslations";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const digits = (phone) => (phone || "").replace(/\D/g, "");
const displayDate = (value, language) => value ? new Date(value).toLocaleString(language === "zh" ? "zh-CN" : language) : "";

export default function WhatsAppInbox({ contacts = [], selectedContact }) {
  const { t, i18n } = useTranslation();
  const m = messageText(i18n.language);
  const [selectedPhone, setSelectedPhone] = useState("");
  const [reply, setReply] = useState("");
  const [manualPhone, setManualPhone] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [templateLanguage, setTemplateLanguage] = useState("pt_PT");
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  useEffect(() => { setSelectedPhone(""); setManualPhone(""); setReply(""); }, [selectedContact?.id]);
  const status = useQuery({ queryKey: ["whatsapp-status"], queryFn: async () =>
    (await api.get("/whatsapp/status")).data, retry: false });
  const conversations = useQuery({ queryKey: ["whatsapp-conversations"], queryFn: async () =>
    (await api.get("/whatsapp/conversations")).data.conversations, enabled: !!status.data?.configured,
    refetchInterval: 15000, retry: false });
  const phone = digits(manualPhone || selectedPhone || selectedContact?.whatsapp || selectedContact?.mobile || selectedContact?.phone);
  const thread = useQuery({ queryKey: ["whatsapp-thread", phone], queryFn: async () =>
    (await api.get(`/whatsapp/conversations/${encodeURIComponent(phone)}`)).data.messages,
    enabled: !!status.data?.configured && !!phone, refetchInterval: 10000, retry: false });
  const matchingContact = contacts.find((item) => [item.whatsapp, item.mobile, item.phone]
    .some((value) => digits(value) === phone));
  const latestInbound = [...(thread.data || [])].reverse().find((message) => message.direction === "inbound");

  if (latestInbound) {
    console.log("WHATSAPP WINDOW DEBUG", {
      rawTimestamp: latestInbound.timestamp,
      parsedTimestamp: new Date(latestInbound.timestamp).toISOString(),
      browserNow: new Date().toISOString(),
      ageHours: (Date.now() - new Date(latestInbound.timestamp).getTime()) / 3600000,
    });
  }

  const withinWindow = latestInbound && Date.now() - new Date(latestInbound.timestamp).getTime() < 24 * 3600 * 1000;

  const [signatureEnabled, setSignatureEnabled] = useState(false);

  const whatsappSignature = `Victor Senra
PANATLANTIK INTERNATIONAL
International Real Estate & Investment Advisory
☎ +351 934 158 151
✉ victor@panatlantik.com
🌐 www.panatlantik.com`;

  const addSignature = () => {
    setSignatureEnabled((current) => !current);
  };

  const deleteMessage = async (messageId) => {
    if (!messageId) return;

    if (!window.confirm(
      "Delete this message from the Panatlantik CRM history?\n\nThis will not delete it from the recipient's WhatsApp."
    )) return;

    try {
      await api.delete(`/whatsapp/messages/${encodeURIComponent(messageId)}`);
      await Promise.all([thread.refetch(), conversations.refetch()]);
      toast.success("Message deleted from CRM");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to delete message");
    }
  };

  const send = async () => {
    if (!phone || !reply.trim() || sending) return;

    if (!window.confirm(m("waConfirm", { phone }))) return;
    setSending(true);
    try {
      const messageBody = signatureEnabled
        ? `${reply.trim()}\n\n${whatsappSignature}`
        : reply.trim();

      await api.post("/whatsapp/send", { to: phone, body: messageBody });
      setReply("");
      setSignatureEnabled(false);
      await Promise.all([thread.refetch(), conversations.refetch()]);
      toast.success(m("waAccepted"));
    } catch (error) {
      toast.error(error.response?.data?.detail || m("waSendError"));
    } finally {
      setSending(false);
    }
  };

  const startWithTemplate = async () => {
    if (!phone || !templateName || !consent || sending) return;
    if (!window.confirm(m("waTemplateConfirm", { name: templateName, phone }))) return;
    setSending(true);
    try {
      await api.post("/whatsapp/send-template", {
        to: phone, name: templateName.trim(), language: templateLanguage.trim(), consent_confirmed: consent,
      });
      setConsent(false);
      await Promise.all([thread.refetch(), conversations.refetch()]);
      toast.success(m("waTemplateAccepted"));
    } catch (error) {
      toast.error(error.response?.data?.detail || m("waTemplateError"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-sm p-6 mt-6" aria-label={m("waInbox")}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="font-serif text-lg">{m("waInbox")}</h3>
        <Button variant="outline" onClick={() => { status.refetch(); conversations.refetch(); if (phone) thread.refetch(); }}>{m("refresh")}</Button>
      </div>
      {status.isError && <p className="text-red-700">{m("waStatusError")}</p>}
      {status.data && !status.data.configured && (
        <p className="text-amber-800 bg-amber-50 p-4">{m("waNotConnected")}</p>
      )}
      {status.data?.configured && <>
        <p className="text-sm text-slate-600 mb-4">{m("waBusiness")}: {status.data.business_number || m("connected")}. {m("waInfo")}</p>
        <label className="block text-sm mb-4">{m("waRecipient")}
          <input type="tel" value={manualPhone} onChange={(event) => { setManualPhone(event.target.value); setSelectedPhone(""); }}
            placeholder={selectedContact?.mobile || "+351..."} className="block mt-1 w-full border border-slate-200 rounded-sm p-2" />
        </label>
        {conversations.isError && <p className="text-red-700">{m("waConversationsError")}</p>}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(220px,1fr)_2fr] gap-4">
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {!conversations.isLoading && !conversations.data?.length && <p className="text-slate-500">{m("waNoConversations")}</p>}
            {(conversations.data || []).map((item) => <button type="button" key={item.phone}
              onClick={() => { setSelectedPhone(item.phone); setReply(""); }}
              className={`block w-full text-left border rounded-sm p-3 hover:bg-slate-50 ${phone === item.phone ? "border-emerald-600" : "border-slate-200"}`}>
              <strong className="block">{contacts.find((c) => [c.whatsapp, c.mobile, c.phone].some((v) => digits(v) === item.phone))?.name || item.name || `+${item.phone}`}</strong>
              <span className="block text-xs text-slate-500">+{item.phone} · {displayDate(item.timestamp, i18n.language)}</span>
              <span className="block text-sm text-slate-600 truncate">{item.last}</span>
            </button>)}
          </div>
          <div className="border rounded-sm p-4 min-h-52">
            {!phone && <p className="text-slate-500">{m("waChoose")}</p>}
            {phone && <>
              <strong>{matchingContact?.name || `+${phone}`}</strong>
              {thread.isError && <p className="text-red-700">{m("waMessagesError")}</p>}
              <div className="max-h-80 overflow-y-auto space-y-3 my-4" aria-live="polite">
                {(thread.data || []).map((item) => <div key={item.id}
                  className={`p-3 rounded-sm whitespace-pre-wrap break-words text-sm ${item.direction === "outbound" ? "bg-emerald-50 ml-8" : "bg-slate-100 mr-8"}`}>
                  {item.direction === "outbound" && item.body?.includes("PANATLANTIK INTERNATIONAL") ? (() => {
                    const marker = "PANATLANTIK INTERNATIONAL";
                    const markerIndex = item.body.indexOf(marker);
                    const messageText = item.body.slice(0, markerIndex).trim();
                    const signatureText = item.body.slice(markerIndex);
                    const signatureLines = signatureText.split("\\n").filter(Boolean);

                    return (
                      <div>
                        {messageText && (
                          <div className="whitespace-pre-wrap break-words mb-3">
                            {messageText}
                          </div>
                        )}

                        <div className="border border-slate-200 bg-white p-4">
                          <div className="flex items-center gap-4">
                            <img
                              src="/panatlantik-logo.png"
                              alt="Panatlantik International"
                              className="w-20 h-20 object-contain shrink-0"
                            />

                            <div className="border-l border-[#C6A15B] pl-4">
                              <div className="font-semibold text-[#0B2545]">
                                Victor Senra
                              </div>
                              <div className="font-semibold text-[#C6A15B] text-xs tracking-wide mt-1">
                                PANATLANTIK INTERNATIONAL
                              </div>
                              <div className="text-xs text-[#0B2545] mt-1">
                                International Real Estate &amp; Investment Advisory
                              </div>
                              <div className="text-xs text-slate-600 mt-2 space-y-1">
                                <div>☎ +351 934 158 151</div>
                                <div>✉ victor@panatlantik.com</div>
                                <div>🌐 www.panatlantik.com</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })() : (
                    <span className="block">{item.body}</span>
                  )}
                  <div className="flex items-center justify-between gap-3 mt-2">
                    <span className="text-xs text-slate-500">
                      {displayDate(item.timestamp, i18n.language)} · {item.direction === "outbound" ? item.status : m("received")}
                    </span>
                    <button
                      type="button"
                      onClick={() => deleteMessage(item.id)}
                      className="text-[11px] font-medium text-[#0B2545] hover:text-[#C6A15B] hover:underline"
                      title="Delete from CRM history"
                    >
                      Delete
                    </button>
                  </div>
                </div>)}
              </div>
              {!withinWindow && <p className="text-amber-800 text-sm mb-2">{m("waWindow")}</p>}
              <Textarea value={reply} maxLength={4096} onChange={(event) => setReply(event.target.value)}
                rows={5} placeholder={m("waWrite")} disabled={!withinWindow || sending}
                className="border-[#C6A15B] focus-visible:ring-[#C6A15B]" />

              {signatureEnabled && (
                <div className="mt-3 border border-[#C6A15B] bg-white p-4">
                  <div className="flex items-center gap-4">
                    <img
                      src="/panatlantik-logo.png"
                      alt="Panatlantik International"
                      className="w-20 h-20 object-contain shrink-0"
                    />
                    <div className="border-l border-[#C6A15B] pl-4">
                      <div className="font-semibold text-[#0B2545]">Victor Senra</div>
                      <div className="font-semibold text-[#C6A15B] text-xs tracking-wide mt-1">
                        PANATLANTIK INTERNATIONAL
                      </div>
                      <div className="text-xs text-[#0B2545] mt-1">
                        International Real Estate &amp; Investment Advisory
                      </div>
                      <div className="text-xs text-slate-600 mt-2">
                        ☎ +351 934 158 151<br />
                        ✉ victor@panatlantik.com<br />
                        🌐 www.panatlantik.com
                      </div>
                    </div>
                  </div>
                </div>
              )}


              <div className="flex flex-wrap items-center gap-2 mt-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={addSignature}
                  disabled={!withinWindow || sending}
                  className="border-[#C6A15B] bg-[#C6A15B] text-[#0B2545] hover:bg-[#B8924E]"
                >
                  {signatureEnabled ? t("whatsappSignatureAdded") : t("whatsappAddSignature")}
                </Button>
                <Button
                  className="bg-[#0B2545] text-white hover:bg-[#081D36]"
                  onClick={send}
                  disabled={!withinWindow || !reply.trim() || sending}
                >
                  {sending ? m("sending") : m("waSend")}
                </Button>
              </div>
              {!withinWindow && <div className="border-t border-slate-200 mt-5 pt-4 space-y-2 text-sm">
                <label className="block">{m("waTemplateName")}
                  <input value={templateName} maxLength={100} onChange={(event) => setTemplateName(event.target.value)}
                    placeholder="hello_world" className="block mt-1 w-full border border-slate-200 rounded-sm p-2" />
                </label>
                <label className="block">{m("waTemplateLanguage")}
                  <input value={templateLanguage} maxLength={12} onChange={(event) => setTemplateLanguage(event.target.value)}
                    placeholder="pt_PT" className="block mt-1 w-full border border-slate-200 rounded-sm p-2" />
                </label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} /> {m("waConsent")}</label>
                <Button type="button" variant="outline" onClick={startWithTemplate}
                  disabled={!consent || !templateName.trim() || sending}>{m("waSendTemplate")}</Button>
              </div>}
            </>}
          </div>
        </div>
      </>}
    </section>
  );
}
